/**
 * Pure logic for the Releases scene's announce-button state machine.
 *
 * The button has three modes — co-located here so they can be unit-tested
 * without rendering the scene (vitest in mops-web runs in `node`, not jsdom).
 *
 * - `announce`     — no MOPS draft is open; the operator can announce the
 *                    pre-release at the previewed head SHA.
 * - `repost-slack` — a MOPS draft is already open at the same SHA; clicking
 *                    re-runs the announce mutation, which the server treats
 *                    as idempotent (no second draft, but Slack re-posts).
 * - `disabled`     — a MOPS draft is open at a different SHA. The operator
 *                    must publish or discard the existing draft on github.com
 *                    before they can announce a fresh one.
 */
export type AnnounceButtonMode = 'announce' | 'repost-slack' | 'disabled';

export interface IAnnounceButtonState {
  mode: AnnounceButtonMode;
  label: string;
  /** Tooltip shown on the disabled state, naming the conflicting SHA. */
  tooltip?: string;
}

interface IDeriveAnnounceButtonStateInput {
  expectedHeadSha: string;
  draftTargetSha: string | null;
  /**
   * Slack channel name (with leading `#`) the announce mutation will post to,
   * threaded through here from the caller because mops-api gates the channel
   * on `IS_DEV` (`#production-deploys-test` in dev, `#production-deploys`
   * elsewhere) — keeping the gate in mops-api means the frontend never lies
   * about where the post is going. Plumbed as a parameter (rather than
   * imported from `env.ts`) so this module stays pure and trivially testable.
   */
  slackChannel: string;
}

export const SHORT_SHA_LENGTH = 7;
const shortSha = (sha: string) => sha.slice(0, SHORT_SHA_LENGTH);

/**
 * Bookkeeping for the "Posted at HH:MM — view in Slack" confirmation line
 * surfaced after a successful announce or re-post. `slackUrl` is null when
 * Slack was unreachable; the announce flow degrades to "draft created, no
 * permalink" rather than failing the whole mutation.
 */
export interface IAnnounceJustPosted {
  postedAt: Date;
  slackUrl: string | null;
}

/**
 * Compute the announce button's mode + label + tooltip from the preview's
 * head SHA and the open draft's target SHA (or `null` when no draft exists).
 */
export function deriveAnnounceButtonState({
  expectedHeadSha,
  draftTargetSha,
  slackChannel,
}: IDeriveAnnounceButtonStateInput): IAnnounceButtonState {
  const announceLabel = `Announce pre-release in ${slackChannel}`;
  if (draftTargetSha === null) {
    return {
      mode: 'announce',
      label: announceLabel,
    };
  }
  if (draftTargetSha === expectedHeadSha) {
    return {
      mode: 'repost-slack',
      label: 'Re-post Slack',
    };
  }
  return {
    mode: 'disabled',
    label: announceLabel,
    tooltip: `A draft for a different SHA (${shortSha(draftTargetSha)}) is already open — view it above.`,
  };
}

/**
 * Format a Date as `HH:MM` (24-hour) in the operator's local timezone — used
 * for the "Posted at HH:MM — view in Slack" confirmation line after announce.
 * Pinned to 24-hour so the inline string stays compact across locales (no
 * trailing AM/PM).
 */
export function formatPostedAtClockTime(timestamp: Date): string {
  return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
}

/**
 * First non-empty line of a multi-line release body — used as the truncated
 * summary in the history table. Returns an empty string when the body has
 * no non-blank lines.
 */
export function summaryFirstLine(body: string): string {
  for (const line of body.split('\n')) {
    const trimmed = line.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return '';
}
